'use client';

import { FC, useState } from 'react';
import {
  FactionLeaderboard,
  FactionLeaderboardSkeleton,
} from './faction-leaderboard';
import { LeaderboardHead } from './leaderboard-head';
import { getFactionLeaderboardTop } from '@wwe-portal/web/services/client/leaderboard';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { MAX_FACTION_LEADERBOARD_LIMIT } from '@wwe-portal/web/utils/constants/leaderboard';
import { FactionLeaderboardRank } from '@wwe-portal/entities/leaderboard.type';
import { QUERY_KEYS_LEADERBOARD_FACTION_TOP } from '@wwe-portal/web/utils/constants/query-key';
import { PaginationBar } from '../../../../../packages/ui/components/pagination/pagination-bar';
import { useResponsive } from '@wwe-portal/ui/hooks/use-responsive';
import { LeaderboardLoadMoreButton } from './leaderboard-loadmore-button';
import { FactionRowHeaderDesktop } from './faction-leaderboard-responsive';

const PER_PAGE = 100;

const TableHeader: FC<{ name: string }> = ({ name }) => (
  <LeaderboardHead hideLiveTag name={name} moreHref="" moreText="" />
);

const Table: FC<{
  data: FactionLeaderboardRank[];
  name: string;
  pointColumn: string;
}> = ({ data, name, pointColumn }) => {
  const columnNames = { point: pointColumn };
  return (
    <FactionLeaderboard
      data={data.map((faction) => ({
        points: faction.factionPoints,
        rank: faction.factionRank,
        factionId: faction.factionId,
        faction: faction.factionName,
        name: faction.factionName,
      }))}
      mobileHeader={<TableHeader name={name} />}
      desktopHeader={<TableHeader name={name} />}
      customColumnNames={columnNames}
      desktopRowHeader={<FactionRowHeaderDesktop customColumn={columnNames} />}
    />
  );
};

const InfiniteTopFactionTable: FC<{
  name: string;
  id: string;
  pointColumn: string;
}> = ({ name, id, pointColumn }) => {
  const { data, fetchNextPage, isFetchingNextPage, hasNextPage } =
    useInfiniteQuery({
      queryKey: [QUERY_KEYS_LEADERBOARD_FACTION_TOP, id],
      queryFn: async ({ pageParam }) => {
        const response = await getFactionLeaderboardTop({
          category: id,
          skip: pageParam * PER_PAGE,
          limit: PER_PAGE,
        });
        return response.data.data.leaderboard;
      },
      getNextPageParam: (lastPage, pages) => {
        if (lastPage.length < PER_PAGE) {
          return undefined;
        }
        if (pages.length * PER_PAGE >= MAX_FACTION_LEADERBOARD_LIMIT) {
          return undefined;
        }
        return pages.length;
      },
      initialPageParam: 0,
    });
  const allData = data?.pages.flatMap((page) => page);

  return (
    <div className="py-10">
      {allData && (
        <Table data={allData} name={name} pointColumn={pointColumn} />
      )}
      {!allData && (
        <FactionLeaderboardSkeleton
          mobileHeader={<TableHeader name={name} />}
          desktopHeader={<TableHeader name={name} />}
          nRows={PER_PAGE}
          desktopRowHeader={
            <FactionRowHeaderDesktop customColumn={{ point: pointColumn }} />
          }
        />
      )}
      {hasNextPage && (
        <LeaderboardLoadMoreButton
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          className="mt-8"
        />
      )}
    </div>
  );
};

const PaginatedTopFactionTable: FC<{
  perPage: number;
  name: string;
  id: string;
  pointColumn: string;
}> = ({ perPage, name, id, pointColumn }) => {
  const [page, setPage] = useState(0);
  const { data } = useQuery({
    queryKey: [
      QUERY_KEYS_LEADERBOARD_FACTION_TOP,
      {
        id,
        page,
        perPage,
      },
    ],
    queryFn: async () => {
      const response = await getFactionLeaderboardTop({
        category: id,
        skip: page * perPage,
        limit: perPage,
      });
      const { leaderboard, totalCount } = response.data.data;
      return { leaderboard, totalCount };
    },
  });
  return (
    <div className="py-10">
      {!data && (
        <FactionLeaderboardSkeleton
          mobileHeader={<TableHeader name={name} />}
          desktopHeader={<TableHeader name={name} />}
          nRows={PER_PAGE}
          desktopRowHeader={
            <FactionRowHeaderDesktop customColumn={{ point: pointColumn }} />
          }
        />
      )}
      {data?.leaderboard && (
        <Table data={data?.leaderboard} name={name} pointColumn={pointColumn} />
      )}
      {data?.totalCount && (
        <div className="mt-8 w-full">
          <PaginationBar
            totalPages={Math.ceil(Number(data.totalCount) / perPage)}
            showPages={5}
            page={page}
            setPage={setPage}
          />
        </div>
      )}
    </div>
  );
};

export const TopFactionTable: FC<{
  name: string;
  id: string;
  pointColumn: string;
}> = ({ name, id, pointColumn }) => {
  const { isHydrated, isMobile } = useResponsive();
  if (!isHydrated) {
    return null;
  }
  if (isMobile) {
    return (
      <InfiniteTopFactionTable name={name} id={id} pointColumn={pointColumn} />
    );
  }
  return (
    <PaginatedTopFactionTable
      perPage={PER_PAGE}
      name={name}
      id={id}
      pointColumn={pointColumn}
    />
  );
};
